import React from 'react';
import {
  HStack,
  Box,
  Text,
  Avatar,
  Icon,
  Divider,
  Wrap,
  WrapItem,
  Badge,
  useColorModeValue
} from '@chakra-ui/react';
import { FiCheck, FiCpu, FiUser } from 'react-icons/fi';
import { colors } from '../../../assets/styles/colors';

export interface ChatMessageProps {
  id: string;
  type: 'user' | 'agent';
  content: string;
  timestamp: Date;
  suggestedWords?: string[];
  onWordSelect?: (word: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  id,
  type,
  content,
  suggestedWords,
  onWordSelect
}) => {
  // UI color values
  const userMessageBg = useColorModeValue('blue.500', 'blue.400');
  const agentMessageBg = useColorModeValue('white', colors.primary[600]);
  const textColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  const successColor = useColorModeValue('green.500', 'green.400');

  return (
    <HStack
      spacing={3}
      align="start"
      justify={type === 'user' ? 'flex-end' : 'flex-start'}
    >
      {type === 'agent' && (
        <Avatar
          size="sm"
          bg={accentColor}
          icon={<Icon as={FiCpu} color="white" />}
        />
      )}

      <Box
        maxW="70%"
        bg={type === 'user' ? userMessageBg : agentMessageBg}
        color={type === 'user' ? 'white' : textColor}
        p={4}
        borderRadius="2xl"
        borderBottomLeftRadius={type === 'agent' ? 'md' : '2xl'}
        borderBottomRightRadius={type === 'user' ? 'md' : '2xl'}
      >
        <Text whiteSpace="pre-line">{content}</Text>

        {suggestedWords && suggestedWords.length > 0 && (
          <Box mt={3}>
            <Divider mb={3} />
            <Text fontSize="sm" fontWeight="bold" mb={2}>
              Added to your collection:
            </Text>
            <Wrap spacing={2}>
              {suggestedWords.map((word, index) => (
                <WrapItem key={index}>
                  <Badge
                    colorScheme="green"
                    variant="subtle"
                    borderRadius="full"
                    px={3}
                    py={1}
                    cursor="pointer"
                    _hover={{ transform: 'scale(1.05)' }}
                    onClick={() => onWordSelect && onWordSelect(word)}
                  >
                    <HStack spacing={1}>
                      <Icon as={FiCheck} fontSize="xs" />
                      <Text fontSize="xs">{word}</Text>
                    </HStack>
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}
      </Box>

      {type === 'user' && (
        <Avatar
          size="sm"
          bg={successColor}
          icon={<Icon as={FiUser} color="white" />}
        />
      )}
    </HStack>
  );
};

export default ChatMessage;